import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef,
  HostListener, OnInit, PLATFORM_ID, ViewChild, inject, input, output,
} from '@angular/core';
import { AirportSearchBase } from '@shared/base/airport-search.base';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { AirportModel } from 'app/airport/models/airport.model';
import { Observable, finalize } from 'rxjs';
import { V2_ICONS } from '@v2/v2.icons';
import { V2AirportPage, V2AirportSearchService } from './v2-airport-search.service';

let nextId = 0;

/**
 * The type-ahead both v2 apps search airports with.
 *
 * It extends the existing `AirportSearchBase`, which already owns the 250ms
 * debounce, pagination and request lifecycle, including pending/failure state
 * and recovery after clearing a search. This view owns selection and navigation.
 */
@Component({
  selector: 'app-v2-airport-search',
  templateUrl: './v2-airport-search.component.html',
  styleUrls: ['./v2-airport-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgIcon],
  providers: [provideIcons(V2_ICONS)],
})
export class V2AirportSearchComponent extends AirportSearchBase implements OnInit {
  private readonly api = inject(V2AirportSearchService);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  readonly placeholder = input('Search by ICAO, IATA, name or city');
  readonly autofocus = input(false);
  readonly airportSelected = output<AirportModel>();

  @ViewChild('field', { static: true }) private field!: ElementRef<HTMLInputElement>;

  protected highlighted = -1;
  protected query = '';

  /** Stable identity for `aria-activedescendant`; several of these can share a page. */
  protected readonly uid = `v2-airport-search-${nextId++}`;

  /** A fixed reference, so the `@for` over it is not a new array on every check. */
  protected readonly skeletonRows = [0, 1, 2];

  ngOnInit(): void {
    this.initAirportSearch();
    if (this.autofocus() && this.isBrowser) { this.field.nativeElement.focus(); }
  }

  protected override fetchAirports(
    params: { page: number; page_size: number; search?: string },
  ): Observable<V2AirportPage> {
    return this.api.search(params).pipe(
      finalize(() => this.cd.markForCheck()),
    );
  }

  protected onInput(value: string): void {
    this.query = value;
    this.highlighted = -1;
    this.onAirportSearch(value);
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.highlighted = Math.min(this.highlighted + 1, this.airports.length - 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.highlighted = Math.max(this.highlighted - 1, 0);
    } else if (event.key === 'Enter' && this.highlighted >= 0) {
      event.preventDefault();
      this.choose(this.airports[this.highlighted]);
    } else if (event.key === 'Escape') {
      this.clear();
    }
  }

  /** Cmd+K / Ctrl+K focuses whichever search field is on screen. */
  @HostListener('document:keydown', ['$event'])
  protected onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.isBrowser) { return; }
    if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      this.field.nativeElement.focus();
    }
  }

  protected choose(airport: AirportModel): void {
    this.airportSelected.emit(airport);
    this.clear();
  }

  private clear(): void {
    this.query = '';
    this.highlighted = -1;
    this.clearAirportSearch();
    this.field.nativeElement.value = '';
    this.cd.markForCheck();
  }
}
